// src/components/layouts/AdminSidebar.jsx
import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Mail,
  Send,
  UserPlus,
  User,
  Archive,
  CheckCircle,
  BookOpen,
  Megaphone,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  HelpCircle,
  Globe,
  FileText,
  Calendar,
  Shield,
  Award,
  Bookmark,
  Clock,
  CheckSquare,
  ClipboardList,
  Wrench
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from "../../utils/axios"
import ALLOWED_ROLES from '../admin/utils/AllowedRoles';
import { showToast } from '../../utils/toast';

const AdminSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const { user, logout, getAvailableDashboards } = useAuth();
  // State for sidebar collapsed status
  const [isCollapsed, setIsCollapsed] = useState(false);
  // State for mobile menu
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [stats, setStats] = useState({ pendingReviews: 12,
    accepted: 0,
    archived: 0,
    decisioned: 0,
    inbox: 0,
    pendingDecisions: 0})


    // Fetch dashboard stats
    useEffect(() => {
      const fetchStats = async () => {
        try {
          const response = await axios.get('/editors/backend/editors/dashboard-stats');
          if (response.data.success) {
            setStats(response.data.stats);
          }
        } catch (error) {
          console.error('Error fetching stats:', error);
          showToast('Failed to load dashboard statistics', 'error');
        }
      };
  
      fetchStats();
    }, []);
  

    // Check if user has required editorial level]
const isAuthorized = user && ALLOWED_ROLES.includes(user.editorial_level || user.editorialLevel);
// Editors-in-Chief and Admins only
const isEicOrAdmin = user && (user.isAdmin === true || ['admin', 'editor_in_chief', 'editor-in-chief'].includes(user.editorial_level || user.editorialLevel));
// Base navigation
const baseNavItems = [
  { 
    path: '/editors/dashboard', 
    icon: LayoutDashboard, 
    label: 'Dashboard',
    badge: null,
  },
  { 
    path: '/editors/inbox', 
    icon: Mail, 
    label: 'Inbox',
    badge: stats.inbox,
  },
  { 
    path: '/editors/editor-invitations', 
    icon: UserPlus, 
    label: 'Editor Invitations',
    badge: null,
  },
  { 
    path: '/editors/submitted-reviews', 
    icon: ClipboardList, 
    label: 'Submitted Reviews',
    badge: stats.pendingDecisions,
  },
  { 
    path: '/dashboard', 
    icon: User, 
    label: "Author's Dashboard",
    external: false,
  },
];

// Admin / authorized items
const adminNavItems = [
  { 
    path: '/editors/admin/authors', 
    icon: Users, 
    label: 'Authors',
    badge: null,
  },

  { 
    path: '/editors/admin/archived', 
    icon: Archive, 
    label: 'Archived Papers',
    badge: stats.archived,
  },
  { 
    path: '/editors/decisioned', 
    icon: CheckCircle, 
    label: 'Decisioned',
    badge: stats.decisioned,
  },


];

// Items restricted to Editors-in-Chief and Admins only
const eicAdminNavItems = [
    { 
    path: '/editors/admin/announcements', 
    icon: Megaphone, 
    label: 'Announcements',
    badge: null,
  },
    { 
    path: '/editors/editors-management', 
    icon: Users, 
    label: 'Editors Management',
    badge: null,
  },
    { 
    path: '/editors/admin/newsletter', 
    icon: Send, 
    label: 'News Letter',
    badge: null,
  },
  { 
    path: '/editors/admin/invitations/management', 
    icon: UserPlus, 
    label: 'Invitations Management',
    badge: null,
  },
  { 
    path: '/editors/admin/manage-publications?tab=supplements', 
    icon: BookOpen, 
    label: 'Manage Publications',
    badge: null,
  },
  { 
    path: '/editors/admin/manage-special-issues', 
    icon: Bookmark, 
    label: 'Manage Special Issues',
    badge: null,
  },
  { 
    path: '/editors/admin/publish', 
    icon: FileText, 
    label: 'Publish',
    badge: null,
  },
  { 
    path: '/editors/admin/manage-editors', 
    icon: Users, 
    label: 'Manage Editors',
    badge: null,
  },
  { 
    path: '/support/dashboard', 
    icon: Wrench, 
    label: 'Support Logs',
    badge: null,
  },
];

// Final navigation
const navItems = [
  ...baseNavItems,
  ...(isAuthorized ? adminNavItems : []),
  ...(isEicOrAdmin ? eicAdminNavItems : [])
];

  // Available dashboards for switching
  const availableDashboards = getAvailableDashboards?.() || [];

  const handleLogout = async () => {  
    await logout();
    navigate('/editors/login');
  };

  // Toggle sidebar collapse
  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Mobile menu toggle
  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  // Close sidebar on mobile
  const closeSidebar = () => {
    setIsMobileOpen(false);
  };

  // Get user initials
  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`;
    }
    if (user?.fullname) {
      const names = user.fullname.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`;
      }
      return names[0][0];
    }
    return 'AD';
  };

  // Get user full name
  const getUserFullName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.fullname) {
      return user.fullname;
    }
    return 'Admin User';
  };

  // Get user role/title
  const getUserRole = () => {
    if (user?.editorialLevel) {
      return user.editorialLevel.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    return 'Editor';
  };

  // Get user section
  const getUserSection = () => {
    if (user?.editorialSection) {
      return user.editorialSection;
    }
    return null;
  };

  // Check if user has permission for a nav item
  const hasPermission = (requiredPermission) => {
    if (!requiredPermission) return true;
    if (requiredPermission === 'editor') return user?.canAccessEditor === true;
    return true;
  };

  // Filter nav items based on permissions
  const filteredNavItems = navItems.filter(item => hasPermission(item.requiredPermission));

  // Render navigation item
  const renderNavItem = (item, index) => {
    return (
      <li key={index}>
        <NavLink
          to={item.path}
          onClick={closeSidebar}
          className={({ isActive }) =>
            `flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-4 py-3 rounded-lg transition-all group relative ${
              isActive 
                ? 'bg-gradient-to-r from-[--asfi-blue] to-[--asfi-purple] text-white shadow-sm' 
                : 'text-gray-700 hover:bg-purple-50'
            }`
          }
          title={isCollapsed ? item.label : ''}
        >
          <div className="flex items-center">
            <item.icon className={`w-5 h-5 flex-shrink-0 ${
              item.path === location.pathname ? 'text-white' : 'text-gray-500 group-hover:text-[--asfi-blue]'
            }`} />
            {!isCollapsed && (
              <span className="ml-3 text-sm font-medium truncate">{item.label}</span>
            )}
          </div>
          {!isCollapsed && item.badge !== null && item.badge > 0 && (
            <span className="ml-2 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold bg-white text-[--asfi-purple]">
              {item.badge}
            </span>
          )}
          {isCollapsed && item.badge !== null && item.badge > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {item.badge}
            </span>
          )}
        </NavLink>
      </li>
    );
  };

  return (
    <>
      {/* Mobile Menu Button (visible on small screens) */}
      <button
        onClick={toggleMobileMenu}
        className="lg:hidden fixed top-20 left-4 z-50 bg-[--asfi-blue] text-white p-2 rounded-lg shadow-lg"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Overlay for mobile */}
      {isMobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-gray-600 bg-opacity-50 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          ${isCollapsed ? 'w-20' : 'w-72'} 
          bg-white shadow-xl
          fixed lg:relative
          ${isMobileOpen ? 'left-0' : '-left-72 lg:left-0'}
          top-0 h-screen z-50
          flex flex-col
          transition-all duration-300 ease-in-out
        `}
      >
        <div className="h-full flex flex-col">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              {isCollapsed ? (
                <div className="w-full flex justify-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-[--asfi-blue] to-[--asfi-purple] rounded-lg flex items-center justify-center">
                    <img src="/assets/images/favicon.png" alt="ASFIRJ" />
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[--asfi-blue] to-[--asfi-purple] rounded-lg flex items-center justify-center flex-shrink-0">
                    <img src="/assets/images/favicon.png" alt="ASFIRJ" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-800 truncate">Editors Dashboard</h2>
                    <p className="text-xs text-gray-500 truncate">Manage submissions</p>
                  </div>
                </div>
              )}
              
              {/* Collapse toggle button - visible on lg screens */}
              <button 
                onClick={toggleSidebar}
                className="hidden lg:flex text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
              >
                {isCollapsed ? 
                  <ChevronRight className="w-5 h-5" /> : 
                  <ChevronLeft className="w-5 h-5" />
                }
              </button>
              
              {/* Close button for mobile */}
              <button 
                onClick={closeSidebar}
                className="lg:hidden text-gray-500 hover:text-gray-700"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* User Profile Summary - Enhanced with more user data */}
          <div className="px-4 py-4 border-b border-gray-200 flex-shrink-0">
            <div className={`flex ${isCollapsed ? 'flex-col items-center' : 'items-start'} space-y-${isCollapsed ? '2' : '0'} p-3 bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200`}>
              <div className={`w-12 h-12 bg-gradient-to-br from-[--asfi-blue] to-[--asfi-purple] rounded-xl flex items-center justify-center shadow-lg flex-shrink-0 ${isCollapsed ? '' : 'mr-3'}`}>
                <span className="text-white font-semibold text-lg">
                  {getUserInitials()}
                </span>
              </div>
              {!isCollapsed && (
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-800 truncate">
                    {getUserFullName()}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1 truncate">
                    {user?.email || 'editor@asfirj.org'}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                      <Shield className="w-3 h-3 mr-1" />
                      {getUserRole()}
                    </span>
          
                  </div>
                  {getUserSection() && (
                    <p className="text-xs text-gray-600 mt-2">
                      <Bookmark className="w-3 h-3 inline mr-1" />
                      Section: {getUserSection()}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Navigation - Scrollable area */}
          <nav className="flex-1 overflow-y-auto py-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <ul className="space-y-1 px-3">
              {filteredNavItems.map((item, index) => renderNavItem(item, index))}
            </ul>

            {/* Help Box - Inside scrollable area */}
            {!isCollapsed && (
              <div className="mt-6 px-3">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <HelpCircle className="w-4 h-4 mr-2 text-[--asfi-blue]" />
                    Help & Resources
                  </h3>
                  <ul className="space-y-2">
                    <li>
                      <a 
                        href="https://asfirj.org/authors.html" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center text-xs text-[--asfi-blue] hover:text-[--asfi-purple] transition-colors"
                      >
                        <BookOpen className="w-3 h-3 mr-2" />
                        Author Instructions
                        <span className="ml-auto text-xs opacity-60">↗</span>
                      </a>
                    </li>
                    <li>
                      <a 
                        href="https://asfirj.org" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center text-xs text-[--asfi-blue] hover:text-[--asfi-purple] transition-colors"
                      >
                        <Globe className="w-3 h-3 mr-2" />
                        Journal Website
                        <span className="ml-auto text-xs opacity-60">↗</span>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {/* Quick Stats - Inside scrollable area */}
            {!isCollapsed && (
              <div className="mt-4 px-3">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-[--asfi-blue]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Quick Stats
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Pending Reviews</span>
                      <span className="font-medium text-[--asfi-purple]">{stats.pendingReviews}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Accepted Papers</span>
                      <span className="font-medium text-[--asfi-purple]">{stats.accepted}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Archived Papers</span>
                      <span className="font-medium text-[--asfi-purple]">{stats.archived}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Inbox</span>
                      <span className="font-medium text-[--asfi-purple]">{stats.inbox}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </nav>

          {/* Logout Button - Fixed at bottom */}
          <div className="p-4 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={handleLogout}
              className={`flex items-center ${isCollapsed ? 'justify-center' : ''} w-full px-4 py-3 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors group`}
              title={isCollapsed ? 'Log Out' : ''}
            >
              <LogOut className={`w-5 h-5 flex-shrink-0 ${isCollapsed ? '' : 'mr-3'}`} />
              {!isCollapsed && (
                <span className="text-sm font-medium">Logout</span>
              )}
            </button>

            {/* Footer text - Only when expanded */}
            {!isCollapsed && (
              <div className="text-center mt-4">
                <p className="text-xs text-gray-500">
                  Editor Portal v2.0
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  &copy; {new Date().getFullYear()} ASFIRJ
                </p>
                {user?.asfiMembershipId && (
                  <p className="text-xs text-gray-400 mt-1">
                    Member ID: {user.asfiMembershipId}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
